import React, { useState, useEffect, useRef } from 'react';
import { DownloadOutlined } from '@ant-design/icons';
import { Table, notification, Row, Col, Breadcrumb, Button, Typography, Select, Form, DatePicker } from 'antd';
import { dataReports } from '../../config';
import dayjs from 'dayjs';
import { intialPaginationState, showingPageTotalRange, formItemLayout, tailFormItemLayout } from '../../common_functions';
import * as userActions from '../../redux/actions/userActions';
import { useDispatch } from 'react-redux';
const { RangePicker } = DatePicker;

const DataReports = props => {
  const formRef = useRef(null);
  const dispatch = useDispatch();
  const [reports, setReports] = useState([]);
  const [pagination, setPagination] = useState(intialPaginationState);
  const [loading, setLoading] = useState(false);
  const [artists, setArtists] = useState([]);
  const [printsObjects, setPrintsObjects] = useState([]);
  const [conceptPortfolios, setConceptPortfolios] = useState([]);
  const [movablePortfolios, setMovablePortfolios] = useState([]);
  const [columns, setColumns] = useState([]);
  const [sortParameters, setSortParameters] = useState('-id');
  //Get collection data
  useEffect(() => {
    (async () => {
    formRef.current.setFieldsValue({ data_report: 'xrf_basic_details' });
    try {
      setLoading(true);
      await getReportsTableData('xrf_basic_details', true);
      await getArtists();
      await getPrintsObjects();
      await getConcetualPortfolio();
      await showMovablePortfolio();
      await getDataReports(pagination);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  })();
  }, []);
  const getArtists = async () => {
    const { data: artistsData } = await props.getItems('Artist', {
      fields: ['id', 'First_Name', 'last_name'],
      sort: 'First_Name',
      limit: -1,
    });
    setArtists(artistsData);
  };
  const getDataReports = async (pagination, filters = { object_type: { _eq: 'print' } }, sortParam = '-id') => {
    const data_report = formRef?.current?.getFieldValue('data_report') || 'xrf_basic_details';
    const offset = pagination.pageSize * (pagination.current - 1);
    const limit = pagination.pageSize;
    const filterData = {
      data_report,
      filters,
      sort: sortParam,
      offset,
      limit,
      meta: 'filter_count',
    };
    const { data: report, meta } = await props.dataReport(filterData);
    setPagination(prevPagination => ({
      ...prevPagination,
      total: meta.filter_count,
    }));
    setReports(report);
  };
  const getPrintObjects = async artistID => {
    try {
      setLoading(true);
      await getPrintsObjects(artistID);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };
  const getPrintsObjects = async (artistID = 'all') => {
    let filters =
      artistID != 'all'
        ? {
            _and: [
              {
                _or: [{ creator: { _eq: artistID } }, { secondary_artist: { _eq: artistID } }],
              },
              { object_type: { _eq: 'print' } },
              { print: { object_name: { _neq: null } } },
            ],
          }
        : {
            object_type: { _eq: 'print' },
            print: { object_name: { _neq: null } },
          };
    let { data: printsObjData } = await dispatch(
      userActions.getItems('collection_obj', {
        fields: ['id', 'print.object_name'],
        sort: 'id',
        filters: filters,
        limit: -1,
      })
    );
    setPrintsObjects(printsObjData);
  };
  const getConcetualPortfolio = async () => {
    const { data: portfolio } = await props.getItems('portfolio', {
      fields: ['id', 'name'],
      limit: '-1',
      sort: 'name',
      filters: { type: { _eq: process.env.conceptual_work_id } },
    });
    setConceptPortfolios(portfolio);
  };
  const showMovablePortfolio = async (conceptID = null) => {
    conceptID && setLoading(true);
    try {
      let filters = !!conceptID ? { conceptual_number: { _eq: conceptID } } : { type: { _eq: process.env.movable_work_id } };
      const { data: movableData } = await props.getItems('portfolio', {
        fields: ['id', 'edition_number'],
        limit: '-1',
        sort: 'edition_number',
        filters: filters,
      });
      setMovablePortfolios(movableData);
    } finally {
      setLoading(false);
    }
  };
  const sortMap = {
    print_object_id: 'print_object_id',
    acquisition: 'acquisition',
    image_title: 'title',
    common_title: 'common_title',
  };
  const handleTableChange = async (pagination, filters, sorter) => {
    setPagination(pagination);
    try {
      setLoading(true);

      // Determine sort param
      let sortParam = '-id'; // default
      if (sorter && sorter.field && sortMap[sorter.field]) {
        const direction = sorter.order === 'descend' ? '-' : '';
        sortParam = `${direction}${sortMap[sorter.field]}`;
      }
      setSortParameters(sortParam);
      await getDataReports(pagination, setFilters(formRef.current.getFieldsValue()), sortParam);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  // const handleTableChange = async (pagination, filters, sorter) => {
  //   setPagination(pagination);
  //   try {
  //     setLoading(true);
  //     await getDataReports(pagination, setFilters(formRef.current.getFieldsValue()));
  //   } catch (error) {
  //     console.log(error);
  //   } finally {
  //     setLoading(false);
  //   }
  // };
  const setFilters = values => {
    let filters = { object_type: { _eq: 'print' } };
    if (values?.artists && values.artists !== 'all') {
      filters['_or'] = [{ creator: { _eq: values.artists } }, { secondary_artist: { _eq: values.artists } }];
    }
    if (values?.print_object && values.print_object !== 'all') {
      filters['id'] = { _eq: values.print_object };
    }
    if (values?.portfolio || values?.edition_number || values?.date_range) {
      filters['_and'] = [];
      if (values?.date_range) {
        filters['_and'].push(
          {
            print: {
              creation_date_start_year: {
                _gte: dayjs(values.date_range[0]).startOf('year').toISOString(),
              },
            },
          },
          {
            print: {
              creation_date_end_year: {
                _lte: dayjs(values.date_range[1]).endOf('year').toISOString(),
              },
            },
          }
        );
      }
      if (values?.portfolio) {
        filters['_and'].push({
          print: { portfolio_add_number: { _eq: values.portfolio } },
        });
      }
      if (values?.edition_number) {
        filters['_and'].push({
          print: { edition_set: { _eq: values.edition_number } },
        });
      }
    }
    return Object.keys(filters).length !== 0 ? { _and: [filters] } : { object_type: { _eq: 'print' } };
  };
  const onFinish = async values => {
    setPagination(intialPaginationState);
    try {
      setLoading(true);
      await getReportsTableData(values?.data_report);
      await getDataReports(pagination, setFilters(values));
    } finally {
      setLoading(false);
    }
  };
  const handleDownload = async () => {
    try {
      setLoading(true);
      let filterData = {
        data_report: formRef?.current?.getFieldValue('data_report') ?? 'xrf_basic_details',
        filters: setFilters(formRef.current.getFieldsValue()),
        sort: sortParameters,
        offset: 0,
        limit: -1,
      };
      await props.dataReport(filterData, true);
    } catch (error) {
      if (error === undefined) {
        notification.info({
          message: 'No data available for download',
          duration: 2,
        });
      } else {
        notification.error({
          message: 'Error occured while generating the csv file!',
          duration: 2,
        });
      }
    } finally {
      setLoading(false);
    }
  };
  const getReportsTableData = async (report, onload = false) => {
    if (!!onload) {
      try {
        setLoading(true);
        await getDataReports(pagination);
      } finally {
        setLoading(false);
      }
    }
    let specificCols = [];
    let commonCols = [
      {
        title: 'Print Object ID',
        dataIndex: 'print_object_id',
        key: 'print_object_id',
        sorter: true,
      },
      { title: 'Acquisition ID', dataIndex: 'acquisition', key: 'acquisition', sorter: true },
      { title: 'Title', dataIndex: 'image_title', key: 'image_title', sorter: true },
      {
        title: 'Common Title',
        dataIndex: 'common_title',
        key: 'common_title',
        sorter: true,
      },
      {
        title: 'Authenticity Confidence Value',
        dataIndex: 'authenticity_confidence_value',
        key: 'authenticity_confidence_value',
      },
      { title: 'Portfolio', dataIndex: 'portfolio', key: 'portfolio' },
      { title: 'Set', dataIndex: 'set', key: 'set' },
      {
        title: 'Year Date Begin',
        dataIndex: 'year_date_begin',
        key: 'year_date_begin',
      },
      {
        title: 'Year Date End',
        dataIndex: 'year_date_end',
        key: 'year_date_end',
      },
    ];
    switch (report) {
      case 'xrf_summary':
        specificCols = [
          {
            title: 'Dmin Sample Area',
            dataIndex: 'dmin_sample_area',
            key: 'dmin_sample_area',
          },
          {
            title: 'Avg Dmin Ba/Sr',
            dataIndex: 'avg_dmin_ba/sr',
            key: 'avg_dmin_ba/sr',
          },
          // { title: 'Avg Dmin Ba/Sr SD', dataIndex: 'avg_dmin_ba/sr_sd', key: 'avg_dmin_ba/sr_sd' },
          { title: 'Dmax Ag', dataIndex: 'dmax_ag', key: 'dmax_ag' },
          { title: 'Dmax Se', dataIndex: 'dmax_se', key: 'dmax_se' },
        ];
        break;
      case 'xrf_basic_details':
        specificCols = [
          {
            title: 'Dmin Sample Area',
            dataIndex: 'dmin_sample_area',
            key: 'dmin_sample_area',
          },
          { title: 'Dmin1 Ba', dataIndex: 'dmin1_ba', key: 'dmin1_ba' },
          {
            title: 'Dmin1 Ba SD',
            dataIndex: 'dmin1_ba+/-',
            key: 'dmin1_ba+/-',
          },
          { title: 'Dmin1 Sr', dataIndex: 'dmin1_sr', key: 'dmin1_sr' },
          {
            title: 'Dmin1 Sr SD',
            dataIndex: 'dmin1_sr+/-',
            key: 'dmin1_sr+/-',
          },
          { title: 'Dmin1 Ag', dataIndex: 'dmin1_ag', key: 'dmin1_ag' },
          { title: 'Dmin1 Se', dataIndex: 'dmin1_se', key: 'dmin1_se' },
          { title: 'Dmin2 Ba', dataIndex: 'dmin2_ba', key: 'dmin2_ba' },
          {
            title: 'Dmin2 Ba SD',
            dataIndex: 'dmin2_ba+/-',
            key: 'dmin2_ba+/-',
          },
          { title: 'Dmin2 Sr', dataIndex: 'dmin2_sr', key: 'dmin2_sr' },
          {
            title: 'Dmin2 Sr SD',
            dataIndex: 'dmin2_sr+/-',
            key: 'dmin2_sr+/-',
          },
          { title: 'Dmin2 Ag', dataIndex: 'dmin2_ag', key: 'dmin2_ag' },
          { title: 'Dmin2 Se', dataIndex: 'dmin2_se', key: 'dmin2_se' },
          { title: 'Dmin3 Ba', dataIndex: 'dmin3_ba', key: 'dmin3_ba' },
          {
            title: 'Dmin3 Ba SD',
            dataIndex: 'dmin3_ba+/-',
            key: 'dmin3_ba+/-',
          },
          { title: 'Dmin3 sr', dataIndex: 'dmin3_sr', key: 'dmin3_sr' },
          {
            title: 'Dmin3 Sr SD',
            dataIndex: 'dmin3_sr+/-',
            key: 'dmin3_sr+/-',
          },
          { title: 'Dmin3 Ag', dataIndex: 'dmin3_ag', key: 'dmin3_ag' },
          { title: 'Dmin3 Se', dataIndex: 'dmin3_se', key: 'dmin3_se' },
          {
            title: 'Avg Dmin Ba',
            dataIndex: 'avg_dmin_ba',
            key: 'avg_dmin_ba',
          },
          {
            title: 'Avg Dmin Sr',
            dataIndex: 'avg_dmin_sr',
            key: 'avg_dmin_sr',
          },
          {
            title: 'Avg Dmin Ba SD',
            dataIndex: 'avg_dmin_ba_sd',
            key: 'avg_dmin_ba_sd',
          },
          {
            title: 'Avg Dmin Sr SD',
            dataIndex: 'avg_dmin_sr_sd',
            key: 'avg_dmin_sr_sd',
          },
          { title: 'Dmax Ba', dataIndex: 'dmax_ba', key: 'dmax_ba' },
          { title: 'Dmax Sr', dataIndex: 'dmax_sr', key: 'dmax_sr' },
          { title: 'Dmax Ag', dataIndex: 'dmax_ag', key: 'dmax_ag' },
          { title: 'Dmax Se', dataIndex: 'dmax_se', key: 'dmax_se' },
          { title: 'Mount Ba', dataIndex: 'mount_ba', key: 'mount_ba' },
          { title: 'Mount Sr', dataIndex: 'mount_sr', key: 'mount_sr' },
          { title: 'Mount Ag', dataIndex: 'mount_ag', key: 'mount_ag' },
          { title: 'Mount Se', dataIndex: 'mount_se', key: 'mount_se' },
        ];
        break;
      case 'oba_details':
        specificCols = [
          { title: '420 M0', dataIndex: '420_m0', key: '420_m0' },
          { title: '420 M1', dataIndex: '420_m1', key: '420_m1' },
          { title: '420 M2', dataIndex: '420_m2', key: '420_m2' },
          { title: '430 M0', dataIndex: '430_m0', key: '430_m0' },
          { title: '430 M1', dataIndex: '430_m1', key: '430_m1' },
          { title: '430 M2', dataIndex: '430_m2', key: '430_m2' },
          { title: '440 M0', dataIndex: '440_m0', key: '440_m0' },
          { title: '440 M1', dataIndex: '440_m1', key: '440_m1' },
          { title: '440 M2', dataIndex: '440_m2', key: '440_m2' },
          { title: '450 M0', dataIndex: '450_m0', key: '450_m0' },
          { title: '450 M1', dataIndex: '450_m1', key: '450_m1' },
          { title: '450 M2', dataIndex: '450_m2', key: '450_m2' },
          { title: '420 M0-M2', dataIndex: '420_m0_m2', key: '420_m0_m2' },
          { title: '420 M1-M2', dataIndex: '420_m1_m2', key: '420_m1_m2' },
          { title: '430 M0-M2', dataIndex: '430_m0_m2', key: '430_m0_m2' },
          { title: '430 M1-M2', dataIndex: '430_m1_m2', key: '430_m1_m2' },
          { title: '440 M0-M2', dataIndex: '440_m0_m2', key: '440_m0_m2' },
          { title: '440 M1-M2', dataIndex: '440_m1_m2', key: '440_m1_m2' },
          { title: '450 M0-M2', dataIndex: '450_m0_m2', key: '450_m0_m2' },
          { title: '450 M1-M2', dataIndex: '450_m1_m2', key: '450_m1_m2' },
          {
            title: '(440 M1-M2)/M2',
            dataIndex: '(440_m1_m2)/m2',
            key: '(440_m1_m2)/m2',
          },
          {
            title: '(450 M1-M2)/M2',
            dataIndex: '(450_m1_m2)/m2',
            key: '(450_m1_m2)/m2',
          },
          {
            title: '(440 M0-M2)/M2',
            dataIndex: '(440_m0_m2)/m2',
            key: '(440_m0_m2)/m2',
          },
          {
            title: '(450 M0-M2)/M2',
            dataIndex: '(450_m0_m2)/m2',
            key: '(450_m0_m2)/m2',
          },
        ];
        break;
      case 'paper_characteristic_summary':
        specificCols = [
          // {
          //   title: 'Avg Dmin Ba/Sr',
          //   dataIndex: 'avg_dmin_ba/sr',
          //   key: 'avg_dmin_ba/sr',
          // },
          // {
          //   title: 'Avg Dmin Ba/Sr SD',
          //   dataIndex: 'avg_dmin_ba/sr_sd',
          //   key: 'avg_dmin_ba/sr_sd',
          // },
          { title: 'Avg Dmin Ba', dataIndex: 'avg_dmin_ba', key: 'avg_dmin_ba' },
          { title: 'Avg Dmin Ba SD', dataIndex: 'avg_dmin_ba_sd', key: 'avg_dmin_ba_sd' },
          { title: 'Avg Dmin Sr', dataIndex: 'avg_dmin_sr', key: 'avg_dmin_sr' },
          { title: 'Avg Dmin Sr Sd', dataIndex: 'avg_dmin_sr_sd', key: 'avg_dmin_sr_sd' },
          { title: 'Dmax Ag', dataIndex: 'dmax_ag', key: 'dmax_ag' },
          { title: 'Dmax Se', dataIndex: 'dmax_se', key: 'dmax_se' },
          { title: '430 M0', dataIndex: '430_m0', key: '430_m0' },
          { title: '430 M1', dataIndex: '430_m1', key: '430_m1' },
          { title: '430 M2', dataIndex: '430_m2', key: '430_m2' },
          { title: '440 M0', dataIndex: '440_m0', key: '440_m0' },
          { title: '440 M1', dataIndex: '440_m1', key: '440_m1' },
          { title: '440 M2', dataIndex: '440_m2', key: '440_m2' },

          // { title: '420 M0-M2', dataIndex: '420_m0_m2', key: '420_m0_m2' },
          // { title: '420 M1-M2', dataIndex: '420_m1_m2', key: '420_m1_m2' },
          // { title: '430 M0-M2', dataIndex: '430_m0_m2', key: '430_m0_m2' },
          // { title: '430 M1-M2', dataIndex: '430_m1_m2', key: '430_m1_m2' },
          // { title: '440 M0-M2', dataIndex: '440_m0_m2', key: '440_m0_m2' },
          // { title: '440 M1-M2', dataIndex: '440_m1_m2', key: '440_m1_m2' },
          // { title: '450 M1-M2', dataIndex: '450_m1_m2', key: '450_m1_m2' },
          // { title: '450 M0-M2', dataIndex: '450_m0_m2', key: '450_m0_m2' },
          // {
          //   title: '(450 M1-M2)/M2',
          //   dataIndex: '(450_m1_m2)/m2',
          //   key: '(450_m1_m2)/m2',
          // },
          // {
          //   title: '(450 M0-M2)/M2',
          //   dataIndex: '(450_m0_m2)/m2',
          //   key: '(450_m0_m2)/m2',
          // },
          { title: 'Gloss', dataIndex: 'gloss', key: 'gloss' },
          { title: 'Weight', dataIndex: 'weight', key: 'weight' },
        ];
        break;
      default:
        break;
    }
    setColumns([...commonCols, ...specificCols]);
  };
  return (
    <Row className={!!loading ? 'disabledComponent' : ''}>
      <Col span={24} className="app-breadcrumb-col">
        <Breadcrumb items={[{ title: "Reports", href: "/reports/data-reports" }, { title: "Data Reports" }]} />
      </Col>
      <Col className="white-paper" span={24}>
        <div className="dashboard">
          <Row gutter={24} className="page-header-section" align="middle">
            <Col xs={12}>
              <Typography.Title level={4} style={{ margin: 0 }}>
                Reports
              </Typography.Title>
            </Col>
            <Col xs={12}>
              <Button icon={<DownloadOutlined />} className="align-right" type="primary" size="small" onClick={handleDownload}>
                Download Csv
              </Button>
            </Col>
          </Row>
          <Form ref={formRef} layout="vertical" onFinish={onFinish} {...formItemLayout} initialValues={{ layout: tailFormItemLayout }}>
            <Row gutter={24}>
              <Col xs={24} sm={5} md={5} lg={5}>
                <Form.Item
                  name="data_report"
                  label="Report"
                  rules={[
                    {
                      required: true,
                      message: 'Select the data report',
                    },
                  ]}
                >
                  <Select placeholder="Select report" showSearch allowClear optionFilterProp="children">
                    {dataReports.map(col => {
                      return <Select.Option key={col.key} value={col.key}>{col.value}</Select.Option>;
                    })}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={5} md={5} lg={5}>
                <Form.Item
                  name="artists"
                  label="Artists"
                  rules={[
                    {
                      required: true,
                      message: 'Artist/Creator is required!',
                    },
                  ]}
                >
                  <Select placeholder="Select Artist" optionFilterProp="children" onChange={getPrintObjects} showSearch allowClear>
                    <Select.Option key="all" value="all">All</Select.Option>
                    {artists.map(col => {
                      return (
                        <Select.Option key={col.id} value={col.id}>
                          {col.First_Name} {col.last_name}
                        </Select.Option>
                      );
                    })}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={5} md={5} lg={5}>
                <Form.Item
                  name="print_object"
                  label="Print/Portfolio"
                  rules={[
                    {
                      required: false,
                      message: 'Print/Portfolio is required!',
                    },
                  ]}
                >
                  <Select placeholder="Select Print/Portfolio" optionFilterProp="children" showSearch allowClear disabled={loading}>
                    <Select.Option key="all" value="all">All</Select.Option>
                    {printsObjects.map(col => {
                      return <Select.Option key={col.id} value={col.id}>{col.print.object_name}</Select.Option>;
                    })}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={5} md={5} lg={5}>
                <Form.Item name="portfolio" label="Portfolio">
                  <Select placeholder="Select Portfolio" optionFilterProp="children" onChange={showMovablePortfolio} showSearch allowClear>
                    {conceptPortfolios.map(col => {
                      return <Select.Option key={col.id} value={col.id}>{col.name}</Select.Option>;
                    })}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={5} md={5} lg={5}>
                <Form.Item name="edition_number" label="Edition/Set #">
                  <Select placeholder="Select Location" optionFilterProp="children" showSearch allowClear>
                    {movablePortfolios.map(col => {
                      return <Select.Option key={col.id} value={col.id}>{col.edition_number}</Select.Option>;
                    })}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={5} md={5} lg={5}>
                <Form.Item name="date_range" label="Year Date Range">
                  <RangePicker picker="year" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={4} md={4} lg={4}>
                <Form.Item>
                  <Button type="primary" size="small" htmlType="submit" style={{ height: '42px', marginTop: '28px' }}>
                    Search
                  </Button>
                </Form.Item>
              </Col>
            </Row>
          </Form>
          <hr />
          <div className="artist-list">
            <Table
              className="no-fixed-last-col"
              scroll={{ x: 'max-content' }}
              columns={columns}
              bordered
              rowKey={record => record.id}
              dataSource={reports}
              pagination={reports.length > 0 ? { ...pagination, ...showingPageTotalRange } : false}
              loading={loading}
              onChange={handleTableChange}
            />
          </div>
        </div>
      </Col>
    </Row>
  );
};
export default DataReports;
